"use client";

import { useEffect, useRef, useState } from "react";
import { useAnchoredPopover } from "@/lib/popover";
import { cn } from "@/components/ui/cn";

export interface MenuOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
}

export interface MenuProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: MenuOption<T>[];
  /** Renders the trigger element; receives whether the menu is currently open. */
  trigger: (state: { open: boolean }) => React.ReactNode;
  align?: "left" | "right";
  /** Which side of the trigger the popover opens on. Defaults to "bottom". */
  placement?: "bottom" | "top";
  className?: string;
  /** Extra classes for the popover panel (e.g. a wider min-width). */
  panelClassName?: string;
  /** Extra non-selectable content rendered below the options. */
  footer?: React.ReactNode;
}

/**
 * Minimal dropdown menu: a trigger that toggles a popover list of options.
 * Closes on outside click, Escape, or selection. Built as a generic
 * primitive so it can back single-select pickers like the locale switcher.
 */
export function Menu<T extends string>({
  value,
  onChange,
  options,
  trigger,
  align = "right",
  placement = "bottom",
  className,
  panelClassName,
  footer,
}: MenuProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fixed-positioned against the trigger and kept inside the viewport.
  useAnchoredPopover(open, rootRef, panelRef, { align, placement });

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: Event) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative inline-block", className)}>
      <div onClick={() => setOpen((o) => !o)}>{trigger({ open })}</div>
      {open && (
        <div
          ref={panelRef}
          role="listbox"
          style={{ maxWidth: "calc(100vw - 24px)", top: 0, left: 0 }}
          className={cn(
            "fixed z-40 min-w-[8.5rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg",
            panelClassName
          )}
        >
          {options.map((o) => {
            const selected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-muted",
                  selected ? "font-medium text-accent" : "text-foreground"
                )}
              >
                {o.label}
                {selected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
          {footer}
        </div>
      )}
    </div>
  );
}
